import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, Edit, Save, Trash2, MapPin, Image as ImageIcon, ChevronUp, ChevronDown, BookOpen } from "lucide-react";
import { toast } from "sonner";
import ImageInput from "../components/ImageInput";

const EMPTY = { slug: "", name: "", state: "", lat: "", lng: "", active: true, cover_image: "", guide_pdf_url: "", guide_cover_url: "", guide_title: "", guide_covers: [] };

export default function AdminCities() {
  const [cities, setCities] = useState([]);
  const [editing, setEditing] = useState(null); // city or {_new: true}
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const refresh = () => api.get("/cities/all").then((r) => setCities(r.data));
  useEffect(() => { refresh(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing({ _new: true }); };
  const openEdit = (c) => { setForm({ ...EMPTY, ...c, guide_covers: Array.isArray(c.guide_covers) ? c.guide_covers : [], lat: c.lat.toString(), lng: c.lng.toString() }); setEditing(c); };

  const addCover = () => setForm((p) => ({ ...p, guide_covers: [...(p.guide_covers || []), { url: "", edition: `Edição ${(p.guide_covers?.length || 0) + 1}`, year: new Date().getFullYear(), order: (p.guide_covers?.length || 0) }] }));
  const updateCover = (idx, patch) => setForm((p) => ({ ...p, guide_covers: p.guide_covers.map((cv, i) => i === idx ? { ...cv, ...patch } : cv) }));
  const removeCover = (idx) => setForm((p) => ({ ...p, guide_covers: p.guide_covers.filter((_, i) => i !== idx).map((cv, i) => ({ ...cv, order: i })) }));
  const moveCover = (idx, dir) => setForm((p) => {
    const arr = [...p.guide_covers];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return p;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...p, guide_covers: arr.map((cv, i) => ({ ...cv, order: i })) };
  });

  const save = async () => {
    setBusy(true);
    try {
      const cleanCovers = (form.guide_covers || [])
        .filter((cv) => cv.url && cv.url.trim())
        .map((cv, i) => ({
          url: cv.url.trim(),
          edition: (cv.edition || "").trim() || null,
          year: cv.year ? parseInt(cv.year, 10) || null : null,
          order: i,
        }));
      const payload = { ...form, lat: parseFloat(form.lat), lng: parseFloat(form.lng), guide_covers: cleanCovers };
      if (editing._new) await api.post("/cities", payload);
      else {
        const { slug, ...patch } = payload;
        await api.patch(`/cities/${editing.slug}`, patch);
      }
      toast.success("Cidade salva");
      setEditing(null);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (slug) => {
    if (!window.confirm("Desativar cidade?")) return;
    try { await api.delete(`/cities/${slug}`); toast.success("Cidade desativada"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-stone-900">Cidades</h1>
          <p className="text-stone-500 text-sm mt-1">Adicione/edite cidades, suas coordenadas (pin do mapa) e imagem de capa.</p>
        </div>
        <div className="flex gap-2">
          <a href="/admin" className="inline-flex items-center px-3 py-2 text-sm text-blue-900 hover:text-blue-950">← Voltar</a>
          <Button onClick={openNew} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="new-city-btn"><Plus size={14} className="mr-1.5"/> Nova cidade</Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cities.map((c) => (
          <Card key={c.slug} className={`overflow-hidden rounded-2xl border ${c.active ? "border-stone-200" : "border-red-200 opacity-60"}`} data-testid={`city-card-${c.slug}`}>
            <div className="aspect-video bg-stone-100 relative">
              {c.cover_image ? (
                <img src={c.cover_image} alt={c.name} className="absolute inset-0 w-full h-full object-cover"/>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-stone-300"><ImageIcon size={40}/></div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-blue-950/80 to-transparent"/>
              <div className="absolute bottom-2 left-3 right-3 text-white">
                <div className="font-display font-bold text-lg">{c.name}</div>
                <div className="text-[11px] opacity-85 flex items-center gap-1"><MapPin size={11}/> {c.state} · {c.lat.toFixed(4)}, {c.lng.toFixed(4)}</div>
              </div>
              {!c.active && <span className="absolute top-2 right-2 text-[10px] uppercase bg-red-500 text-white px-2 py-0.5 rounded">Inativa</span>}
            </div>
            <div className="p-3 flex items-center justify-between">
              <span className="text-xs text-stone-500 font-mono">{c.slug}</span>
              <div className="flex gap-1">
                <button onClick={() => openEdit(c)} className="p-1.5 text-stone-500 hover:text-stone-900" data-testid={`edit-city-${c.slug}`}><Edit size={14}/></button>
                <button onClick={() => remove(c.slug)} className="p-1.5 text-red-500 hover:text-red-700"><Trash2 size={14}/></button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {editing && (
        <Dialog open onOpenChange={(v) => !v && setEditing(null)}>
          <DialogContent className="max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">{editing._new ? "Nova cidade" : `Editar ${editing.name}`}</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-4">
              {form.cover_image && (
                <div className="sm:col-span-2 aspect-video rounded-xl overflow-hidden bg-stone-100">
                  <img src={form.cover_image} alt="preview" className="w-full h-full object-cover"/>
                </div>
              )}
              <div className="sm:col-span-2"><Label>Imagem de capa da cidade</Label>
                <div className="mt-1"><ImageInput value={form.cover_image} onChange={(v) => setForm({...form, cover_image: v})} aspect="16/9" testid="cf-cover" showPreview={false}/></div>
              </div>
              <div><Label>Slug (URL)</Label><Input required disabled={!editing._new} value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})} placeholder="aracaju" className="rounded-xl mt-1 font-mono" data-testid="cyf-slug"/></div>
              <div><Label>Nome</Label><Input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-xl mt-1" data-testid="cyf-name"/></div>
              <div><Label>Estado (sigla)</Label><Input required value={form.state} onChange={(e) => setForm({...form, state: e.target.value.toUpperCase()})} maxLength={2} className="rounded-xl mt-1" data-testid="cyf-state"/></div>
              <div className="flex items-center gap-2 mt-6"><Switch checked={!!form.active} onCheckedChange={(v) => setForm({...form, active: v})} data-testid="cyf-active"/><Label>Ativa</Label></div>
              <div><Label className="flex items-center gap-1.5"><MapPin size={13} className="text-blue-900"/> Latitude</Label><Input required value={form.lat} onChange={(e) => setForm({...form, lat: e.target.value})} placeholder="-10.9472" className="rounded-xl mt-1 font-mono" data-testid="cyf-lat"/></div>
              <div><Label>Longitude</Label><Input required value={form.lng} onChange={(e) => setForm({...form, lng: e.target.value})} placeholder="-37.0731" className="rounded-xl mt-1 font-mono" data-testid="cyf-lng"/></div>
              <p className="sm:col-span-2 text-[11px] text-stone-500 -mt-2">Use <a href="https://www.openstreetmap.org" target="_blank" rel="noreferrer" className="underline">openstreetmap.org</a> para encontrar coordenadas precisas (botão direito → &quot;Mostrar endereço&quot;).</p>

              <div className="sm:col-span-2 mt-2 p-4 rounded-xl border border-amber-200 bg-amber-50/60 space-y-3">
                <p className="text-xs font-bold uppercase tracking-wider text-amber-900">📖 Guia digital da cidade (PDF)</p>
                <div>
                  <Label className="text-xs">Título do guia (opcional)</Label>
                  <Input value={form.guide_title} onChange={(e) => setForm({...form, guide_title: e.target.value})} placeholder={`Guia ${form.name || "da Cidade"}`} className="rounded-xl mt-1 bg-white" data-testid="cyf-guide-title"/>
                </div>
                <div>
                  <Label className="text-xs">PDF do guia (envie arquivo ou cole link Google Drive/Dropbox)</Label>
                  <div className="mt-1 flex gap-2">
                    <Input value={form.guide_pdf_url} onChange={(e) => setForm({...form, guide_pdf_url: e.target.value})} placeholder="https://..." className="rounded-xl bg-white font-mono text-xs flex-1" data-testid="cyf-guide-pdf"/>
                    <input id="cyf-guide-pdf-file" type="file" accept="application/pdf" hidden onChange={async (e) => {
                      const f = e.target.files?.[0]; if (!f) return;
                      try {
                        const fd = new FormData(); fd.append("file", f);
                        // re-use uploads endpoint; backend currently accepts images only — add PDF support via .ext check (now supports pdf via additional ext list)
                        const { data } = await import("../lib/api").then((m) => m.default.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } }));
                        const url = (process.env.REACT_APP_BACKEND_URL || "") + data.url;
                        setForm((prev) => ({...prev, guide_pdf_url: url}));
                        toast.success("PDF enviado");
                      } catch (err) { toast.error("Falha no upload do PDF"); }
                      finally { e.target.value = ""; }
                    }}/>
                    <Button type="button" variant="outline" onClick={() => document.getElementById("cyf-guide-pdf-file")?.click()} className="rounded-xl shrink-0">Enviar PDF</Button>
                  </div>
                  <p className="text-[10px] text-stone-500 mt-1">Tamanho máx 80 MB. Para PDFs grandes, prefira o upload direto (Google Drive limita downloads de arquivos {'>'} 25 MB e pode falhar em algumas páginas).</p>
                </div>
                <div>
                  <Label className="text-xs">Capa do guia (imagem)</Label>
                  <div className="mt-1"><ImageInput value={form.guide_cover_url} onChange={(v) => setForm({...form, guide_cover_url: v})} aspect="3/4" testid="cyf-guide-cover" showPreview={false}/></div>
                </div>
                {form.guide_cover_url && (
                  <div className="aspect-[3/4] max-w-[140px] rounded-lg overflow-hidden bg-stone-100 border border-stone-200">
                    <img src={form.guide_cover_url} alt="capa" className="w-full h-full object-cover"/>
                  </div>
                )}
                <p className="text-[10px] text-stone-500">A capa acima é a &quot;capa principal&quot; (legado). Use as <strong>edições</strong> abaixo para cadastrar várias capas (uma por edição publicada).</p>
              </div>

              <div className="sm:col-span-2 mt-2 p-4 rounded-xl border border-blue-200 bg-blue-50/40 space-y-3" data-testid="city-covers-editor">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-xs font-bold uppercase tracking-wider text-blue-900 flex items-center gap-1.5"><BookOpen size={13}/> Edições impressas (capas)</p>
                  <Button type="button" size="sm" variant="outline" onClick={addCover} className="rounded-lg text-xs" data-testid="add-cover-btn">
                    <Plus size={13} className="mr-1"/> Adicionar edição
                  </Button>
                </div>
                {(form.guide_covers || []).length === 0 ? (
                  <p className="text-xs text-stone-500 italic">Nenhuma edição cadastrada ainda. Clique em &quot;Adicionar edição&quot; para cadastrar a capa de cada edição publicada.</p>
                ) : (
                  <div className="space-y-3">
                    {form.guide_covers.map((cv, idx) => (
                      <div key={idx} className="flex gap-3 p-3 rounded-xl bg-white border border-stone-200" data-testid={`cover-row-${idx}`}>
                        <div className="w-20 shrink-0">
                          <div className="aspect-[3/4] rounded-lg overflow-hidden bg-stone-100 border border-stone-200 mb-1">
                            {cv.url ? <img src={cv.url} alt={`capa ${idx+1}`} className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center text-stone-300"><ImageIcon size={20}/></div>}
                          </div>
                          <ImageInput value={cv.url} onChange={(v) => updateCover(idx, { url: v })} aspect="3/4" testid={`cover-img-${idx}`} showPreview={false} compact/>
                        </div>
                        <div className="flex-1 grid grid-cols-2 gap-2 content-start">
                          <div className="col-span-2">
                            <Label className="text-[11px]">Nome da edição</Label>
                            <Input value={cv.edition || ""} onChange={(e) => updateCover(idx, { edition: e.target.value })} placeholder={`Edição ${idx + 1}`} className="rounded-lg mt-0.5 h-8 text-sm" data-testid={`cover-edition-${idx}`}/>
                          </div>
                          <div>
                            <Label className="text-[11px]">Ano</Label>
                            <Input type="number" value={cv.year || ""} onChange={(e) => updateCover(idx, { year: e.target.value })} placeholder="2026" className="rounded-lg mt-0.5 h-8 text-sm font-mono" data-testid={`cover-year-${idx}`}/>
                          </div>
                          <div className="flex items-end gap-1 justify-end">
                            <button type="button" onClick={() => moveCover(idx, -1)} disabled={idx === 0} className="p-1.5 rounded hover:bg-stone-100 disabled:opacity-30" data-testid={`cover-up-${idx}`} title="Subir"><ChevronUp size={14}/></button>
                            <button type="button" onClick={() => moveCover(idx, +1)} disabled={idx === form.guide_covers.length - 1} className="p-1.5 rounded hover:bg-stone-100 disabled:opacity-30" data-testid={`cover-down-${idx}`} title="Descer"><ChevronDown size={14}/></button>
                            <button type="button" onClick={() => removeCover(idx)} className="p-1.5 rounded text-red-500 hover:bg-red-50" data-testid={`cover-remove-${idx}`} title="Remover"><Trash2 size={14}/></button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button onClick={save} disabled={busy} className="bg-blue-900 hover:bg-blue-950 text-white" data-testid="cyf-save"><Save size={14} className="mr-1.5"/>{busy ? "Salvando..." : "Salvar"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
